import { buildCollection, buildProperty } from "@firecms/core";

import {
  hasAnyRole,
  ONBOARDING_QUEUE_ROLES,
  ONBOARDING_VIEW_ROLES,
} from "./apple_delegated_onboarding.roles";
import {
  LIFECYCLE_SUBMIT_STATE_LABELS,
  SHOWCASE_LIFECYCLE_ACTION_LABELS,
  SHOWCASE_LIFECYCLE_LABEL,
  SHOWCASE_LIFECYCLE_REQUESTS_PATH,
  SHOWCASE_LIFECYCLE_STATE_LABELS,
  ShowcaseLifecycleRequestEntity,
} from "./apple_showcase_lifecycle.types";
import { stripLifecycleFieldsTheFormMayNotWrite } from "./apple_showcase_lifecycle.utils";

/**
 * `lifecycle_requests` — one document per extend / deactivate / delete attempt on a showcase.
 *
 * 🛑 THE ONLY DESTRUCTIVE SURFACE IN THIS CONSOLE. Every other collection here records something being
 * created. These documents ask the backend to change or remove a showcase that may be displaying right
 * now on a real business's Apple place card, and none of those changes can be undone.
 *
 * ⚠️ EVERY FIELD IS READ-ONLY, INCLUDING THE ONES THIS CONSOLE OWNS, and that is not an oversight. A
 * lifecycle request is written EXCLUSIVELY by the two-step confirmation dialog on the parent showcase
 * request's "Lifecycle" tab, which is where the gates and the "this cannot be undone" copy live. An
 * editable `action` dropdown would be a one-click way past all of it — turning a queued extension into
 * a queued deletion without anyone confirming anything. This collection exists to READ the history of
 * what was asked and what Apple did.
 *
 * ⚠️ `create` IS DENIED FOR THE SAME REASON. There is no legitimate way to author one of these by
 * hand: it would carry no confirmed snapshot and would not have been through a confirmation.
 *
 * ⚠️ `delete` IS DENIED OUTRIGHT — narrower than the parent collection, which lets the queue roles
 * remove a request. This is the audit trail for an irreversible action against a client's public
 * presence, and it is the only record of who asked for it. Nothing in this console may remove it.
 */
export const appleShowcaseLifecycleRequestsCollection = buildCollection<
  Partial<ShowcaseLifecycleRequestEntity>
>({
  id: SHOWCASE_LIFECYCLE_REQUESTS_PATH,
  path: SHOWCASE_LIFECYCLE_REQUESTS_PATH,
  name: "Lifecycle history",
  singularName: SHOWCASE_LIFECYCLE_LABEL,
  description:
    "Every extend, deactivate and delete asked for on this showcase, and what Apple actually did. Read-only: these are created by the confirmation dialog on the Lifecycle tab, never by hand.",
  icon: "history",
  permissions: ({ authController }) => ({
    read: hasAnyRole(authController, ONBOARDING_VIEW_ROLES),
    // Nothing may be edited or authored here — see the collection doc. The dialog is the only writer.
    edit: false,
    create: false,
    // Not even the queue roles. This is the audit trail for an action with no undo.
    delete: false,
  }),
  callbacks: {
    // Belt and braces behind `edit: false`: if a future change re-enables the form, a save still can
    // only carry the fields this console owns.
    onPreSave: ({ values }) => stripLifecycleFieldsTheFormMayNotWrite(values),
  },
  // ⚠️ Must list EVERY property key: `getFormFieldKeys` filters the entity form through this array
  // too, so an omitted key disappears from the detail view, not only from the table.
  propertiesOrder: [
    "action",
    "submitState",
    "contractState",
    "humanActionRequired",
    "humanActionMessage",
    "requestedEndDate",
    "deleteCreative",
    "creativeDeleted",
    "appleObservedState",
    "appleObservedLiveness",
    "confirmedShowcaseId",
    "confirmedLocationId",
    "confirmedBrandId",
    "confirmedStartDate",
    "confirmedEndDate",
    "requestedBy",
    "requestedAt",
    "completedAt",
    "writeError",
  ],
  properties: {
    action: buildProperty({
      name: "Action",
      description: "What was asked for. Set by the confirmation dialog and never editable here.",
      readOnly: true,
      dataType: "string",
      enumValues: SHOWCASE_LIFECYCLE_ACTION_LABELS,
    }),
    submitState: buildProperty({
      name: "Queue status",
      description: "Written as QUEUED by the confirmation dialog; the backend drain advances it.",
      readOnly: true,
      dataType: "string",
      enumValues: LIFECYCLE_SUBMIT_STATE_LABELS,
    }),
    contractState: buildProperty({
      name: "Outcome",
      description:
        "What the backend concluded. “Refused” and “Apple refused it” both mean the showcase is unchanged. “Outcome unknown” means we cannot tell what Apple did — read the note below it and check Apple Business Connect rather than trying again.",
      readOnly: true,
      dataType: "string",
      enumValues: SHOWCASE_LIFECYCLE_STATE_LABELS,
    }),
    humanActionRequired: buildProperty({
      name: "Needs a person",
      description:
        "True when nobody should retry this and someone must read Apple's own record first. Set by the backend.",
      readOnly: true,
      dataType: "boolean",
    }),
    humanActionMessage: buildProperty({
      name: "What happened / what to check",
      description: "The backend's explanation, written for a person rather than for a log.",
      readOnly: true,
      dataType: "string",
      multiline: true,
    }),
    requestedEndDate: buildProperty({
      name: "Requested end date (EXCLUSIVE)",
      description:
        "Extensions only. EXCLUSIVE, as everywhere in this console: the showcase is not visible on this date.",
      readOnly: true,
      hideFromCollection: true,
      dataType: "string",
    }),
    deleteCreative: buildProperty({
      name: "Creative removal was requested",
      readOnly: true,
      hideFromCollection: true,
      dataType: "boolean",
    }),
    creativeDeleted: buildProperty({
      name: "Creative was removed",
      description:
        "False on a delete that removed the showcase but not its creative — an unreferenced creative is harmless, but this is the record that one exists.",
      readOnly: true,
      hideFromCollection: true,
      dataType: "boolean",
    }),
    appleObservedState: buildProperty({
      name: "Apple state before the action",
      description:
        "What Apple reported when the backend read the showcase back, moments before acting. After a delete this is the only surviving record of it.",
      readOnly: true,
      hideFromCollection: true,
      dataType: "string",
    }),
    appleObservedLiveness: buildProperty({
      name: "Was it live?",
      description:
        "The backend's verdict: LIVE, NOT_LIVE, or INDETERMINATE when Apple's answer did not settle it. Deletes are refused on anything but NOT_LIVE.",
      readOnly: true,
      hideFromCollection: true,
      dataType: "string",
    }),
    confirmedShowcaseId: buildProperty({
      name: "Showcase id (as confirmed)",
      description:
        "The id the operator typed to confirm. A snapshot of what they were looking at — the backend re-resolves everything and does not trust it.",
      readOnly: true,
      dataType: "string",
    }),
    confirmedLocationId: buildProperty({
      name: "Apple location id (as shown)",
      readOnly: true,
      hideFromCollection: true,
      dataType: "string",
    }),
    confirmedBrandId: buildProperty({
      name: "Apple brand id (as shown)",
      readOnly: true,
      hideFromCollection: true,
      dataType: "string",
    }),
    confirmedStartDate: buildProperty({
      name: "Start date (as shown)",
      readOnly: true,
      hideFromCollection: true,
      dataType: "string",
    }),
    confirmedEndDate: buildProperty({
      name: "End date (as shown, EXCLUSIVE)",
      readOnly: true,
      hideFromCollection: true,
      dataType: "string",
    }),
    requestedBy: buildProperty({
      name: "Asked for by",
      description:
        "The signed-in user who confirmed. For an irreversible action, the record needs a name.",
      readOnly: true,
      dataType: "string",
    }),
    requestedAt: buildProperty({
      name: "Asked for at",
      readOnly: true,
      dataType: "date",
    }),
    completedAt: buildProperty({
      name: "Finished at",
      readOnly: true,
      hideFromCollection: true,
      dataType: "date",
    }),
    writeError: buildProperty({
      name: "Could not queue",
      description:
        "Set by this console when even the queue write failed — “I could not enqueue”. Nothing was sent to Apple. The backend never reads it.",
      readOnly: true,
      hideFromCollection: true,
      dataType: "string",
    }),
  },
});

/**
 * Who may ask for a lifecycle action. Deliberately the SAME narrow list as the queue buttons
 * (`ONBOARDING_QUEUE_ROLES`, currently admin only) rather than the wider view list: every one of these
 * ends in an irreversible change to a client's public Apple presence.
 *
 * ⚠️ A UI CONVENTION, NOT AN ENFORCED CONTROL — the identical caveat `apple_delegated_onboarding.roles.ts`
 * records applies here, and matters more: the Firestore rules let any authenticated principal write.
 * What makes this safe is that the backend re-validates and re-gates everything server-side, against
 * Apple's own current record, before it calls anything.
 */
export const SHOWCASE_LIFECYCLE_ROLES = ONBOARDING_QUEUE_ROLES;
